import * as React from "react";

import { cn } from "../../lib/utils";

const Table = React.forwardRef(({ className, ...props }, ref) => (
  <div className="relative w-full h-full min-h-0 overflow-auto">
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm", className)}
      {...props}
    />
  </div>
));
Table.displayName = "Table";

const TableHeader = React.forwardRef(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      "sticky top-0 z-10 bg-white [&_tr]:border-b",
      className
    )}
    {...props}
  />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
      className
    )}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
      className
    )}
    {...props}
  />
));
TableRow.displayName = "TableRow";

// ---- Column resizing (shared by every table that uses TableHead) ----
// Nothing changes until the user drags a column edge: tables keep their
// default (auto) widths. On the first drag, every header cell of that table
// is frozen at its current rendered width and the table switches to fixed
// layout, so afterwards columns only change when dragged (no auto-stretch).
// The table's width grows/shrinks with the dragged column, so the existing
// overflow-auto wrapper scrolls horizontally when it gets wider than the
// screen. Widths live only in this rendered table (inline styles), nothing
// is persisted, and pinned (left/right sticky) columns are not resizable
// because their offsets depend on fixed widths.
const MIN_COLUMN_WIDTH = 60;
const KEYBOARD_RESIZE_STEP = 10;

const setImportant = (el, prop, value) => el.style.setProperty(prop, value, "important");

const freezeColumnWidths = (table, headerRow) => {
  if (table.dataset.columnsResized === "true") return;
  let total = 0;
  Array.from(headerRow.children).forEach((cell) => {
    const width = cell.getBoundingClientRect().width;
    total += width;
    setImportant(cell, "width", `${width}px`);
  });
  setImportant(table, "table-layout", "fixed");
  setImportant(table, "width", `${total}px`);
  setImportant(table, "min-width", `${total}px`);
  table.dataset.columnsResized = "true";
};

// Back to the default auto widths (used when the set of columns changes).
const resetColumnWidths = (table) => {
  if (!table || table.dataset.columnsResized !== "true") return;
  ["table-layout", "width", "min-width"].forEach((prop) => table.style.removeProperty(prop));
  table.querySelectorAll("thead th").forEach((cell) => {
    if (cell.dataset.resizableColumn === "true") cell.style.removeProperty("width");
  });
  delete table.dataset.columnsResized;
};

const setColumnWidth = (th, table, startWidth, startTableWidth, requestedWidth) => {
  const floor = Math.min(MIN_COLUMN_WIDTH, startWidth);
  const width = Math.max(floor, requestedWidth);
  setImportant(th, "width", `${width}px`);
  const tableWidth = startTableWidth + (width - startWidth);
  setImportant(table, "width", `${tableWidth}px`);
  setImportant(table, "min-width", `${tableWidth}px`);
};

const beginResize = (th) => {
  const table = th.closest("table");
  if (!table) return null;
  freezeColumnWidths(table, th.parentElement);
  return {
    table,
    startWidth: th.getBoundingClientRect().width,
    startTableWidth: table.getBoundingClientRect().width,
  };
};

const onResizePointerDown = (event) => {
  if (event.button !== 0) return;
  event.preventDefault();
  event.stopPropagation();

  const handle = event.currentTarget;
  const th = handle.parentElement;
  const state = beginResize(th);
  if (!state) return;

  const startX = event.clientX;
  const previousCursor = document.body.style.cursor;
  const previousUserSelect = document.body.style.userSelect;
  document.body.style.cursor = "col-resize";
  document.body.style.userSelect = "none";
  handle.dataset.resizing = "true";

  const onMove = (e) =>
    setColumnWidth(th, state.table, state.startWidth, state.startTableWidth, state.startWidth + e.clientX - startX);
  const onUp = () => {
    document.removeEventListener("pointermove", onMove);
    document.removeEventListener("pointerup", onUp);
    document.body.style.cursor = previousCursor;
    document.body.style.userSelect = previousUserSelect;
    delete handle.dataset.resizing;
  };
  document.addEventListener("pointermove", onMove);
  document.addEventListener("pointerup", onUp);
};

const onResizeKeyDown = (event) => {
  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
  event.preventDefault();
  event.stopPropagation();
  const th = event.currentTarget.parentElement;
  const state = beginResize(th);
  if (!state) return;
  const delta = event.key === "ArrowRight" ? KEYBOARD_RESIZE_STEP : -KEYBOARD_RESIZE_STEP;
  setColumnWidth(th, state.table, state.startWidth, state.startTableWidth, state.startWidth + delta);
};

const TableHead = React.forwardRef(({ className, children, resizable = true, ...props }, ref) => {
  const thRef = React.useRef(null);
  const setRefs = React.useCallback(
    (node) => {
      thRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );

  // Pinned (sticky left/right) columns keep their fixed widths.
  const pinned = props.style?.left !== undefined || props.style?.right !== undefined;
  const canResize = resizable && !pinned && !(props.colSpan > 1);

  // If a column appears or disappears (e.g. via the Columns menu) after the
  // user resized this table, fall back to the default auto widths so the
  // new column set lays out normally.
  React.useLayoutEffect(() => {
    const table = thRef.current?.closest("table");
    resetColumnWidths(table);
    return () => resetColumnWidths(table);
  }, []);

  return (
    <th
      ref={setRefs}
      data-resizable-column={canResize ? "true" : undefined}
      className={cn(
        // Sticky is applied per-cell (not just on <thead>) because Tailwind's
        // preflight sets `border-collapse: collapse` on every <table>, and
        // `position: sticky` on a <thead>/<tr> (row-group level) is unreliable
        // with border-collapse in several browsers even though the CSS looks
        // correct — sticking it to each <th> individually is the standard,
        // reliable fix and still merges correctly with columns that are also
        // horizontally pinned (e.g. Record View's left/right sticky columns).
        "sticky top-0 z-10 bg-white h-8 px-2 py-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className
      )}
      {...props}
    >
      {children}
      {canResize && (
        <span
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize column"
          tabIndex={0}
          onPointerDown={onResizePointerDown}
          onKeyDown={onResizeKeyDown}
          onClick={(e) => e.stopPropagation()}
          className="group/resize absolute right-0 top-0 z-[1] flex h-full w-2 cursor-col-resize touch-none select-none justify-end outline-none"
        >
          <span className="my-auto h-1/2 w-px bg-gray-200 group-hover/resize:bg-nbl-primary group-focus-visible/resize:bg-nbl-primary group-data-[resizing=true]/resize:bg-nbl-primary" />
        </span>
      )}
    </th>
  );
});
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "py-1.5 px-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props}
  />
));
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
